import { ActionsOperators } from '../data-rules-model';
import { IActionFieldMeta } from '../meta-data.service';

export class AllowedActions {

  public static actionAllowed(action: ActionsOperators,
                              selectedFieldMeta: IActionFieldMeta,
                              srcMeta: IActionFieldMeta,
                              dstMeta: IActionFieldMeta): boolean {
    switch (action) {
      // Each case is applying a filter to remove the action from the list
      case ActionsOperators.COPY:
        // `canCopyNullValues` is skipped for TO_ONE to allow copying of an optional field to a non-optional field.
        // This allows creating for example a JobSubmission rule where JobOrder responseUser is copied to Candidate owner.
        // Otherwise, Candiate.owner will not appear in the destination drop-down.
        return (this.isScalar(srcMeta) || selectedFieldMeta.metaType === 'TO_ONE')
          && this.isNotReadOnly(dstMeta)
          && (selectedFieldMeta.metaType === 'TO_ONE' || this.canCopyNullValues(srcMeta, dstMeta))
          && !this.supportedReplaceEntriesFields(selectedFieldMeta);
      case ActionsOperators.MOVE:
        return this.isScalar(srcMeta)
          && this.isNotReadOnly(dstMeta)
          && this.canCopyNullValues(srcMeta, dstMeta)
          && !srcMeta.readOnly
          && srcMeta.optional;
      case ActionsOperators.SWAP:
        return this.isScalar(srcMeta)
          && this.isNotReadOnly(dstMeta)
          && !srcMeta.readOnly
          && this.bothOrNeitherAreOptional(srcMeta, dstMeta)
          && !this.supportedReplaceEntriesFields(selectedFieldMeta);
      case ActionsOperators.COUNT:
        return selectedFieldMeta.metaType === 'TO_MANY' && this.isNotReadOnly(dstMeta) && selectedFieldMeta.isSecondaryEntity !== true;
      // for all the following actions, the 'selected field' is the only one under consideration.
      // it's treated as the destination for the purpose of determining which actions are allowed.
      // the value currently in the form as the destination is actually irrelevant when this function
      // is called for actions other than copy/move/swap.
      case ActionsOperators.NULLIFY:
        return (this.isScalar(selectedFieldMeta) || selectedFieldMeta.metaType === 'ADDRESS')
          && !selectedFieldMeta.readOnly && selectedFieldMeta.optional && selectedFieldMeta.metaType !== 'BOOLEAN';
      case ActionsOperators.REPLACE:
        return ((this.isScalar(selectedFieldMeta) || selectedFieldMeta.metaType === 'ADDRESS') && !selectedFieldMeta.readOnly);
      case ActionsOperators.REPLACE_RELATIVE:
        return (selectedFieldMeta.metaType === 'DATE' && !selectedFieldMeta.readOnly);
      case ActionsOperators.APPEND:
        return ((selectedFieldMeta.metaType === 'STRING' || selectedFieldMeta.metaType === 'ADDRESS') && !selectedFieldMeta.readOnly);
      case ActionsOperators.MATHS:
        return ((selectedFieldMeta.metaType === 'INTEGER'
          || selectedFieldMeta.metaType === 'BIG_DECIMAL'
          || selectedFieldMeta.metaType === 'DOUBLE'
          || selectedFieldMeta.metaType === 'FLOAT') && !selectedFieldMeta.readOnly);
      case ActionsOperators.PREPEND:
        return ((selectedFieldMeta.metaType === 'STRING' || selectedFieldMeta.metaType === 'ADDRESS') && !selectedFieldMeta.readOnly);
      case ActionsOperators.SUBSTITUTE:
        return ((selectedFieldMeta.metaType === 'STRING' || selectedFieldMeta.metaType === 'ADDRESS') && !selectedFieldMeta.readOnly);
      case ActionsOperators.ADD:
      case ActionsOperators.REMOVE:
        return selectedFieldMeta && selectedFieldMeta.metaType === 'TO_MANY' && !selectedFieldMeta.readOnly; // is no association 'null'?
      case ActionsOperators.CHANGE:
        return selectedFieldMeta && selectedFieldMeta.metaType === 'TO_ONE' && !selectedFieldMeta.readOnly
          && !this.notSupportedChangeEntriesField(selectedFieldMeta); // is no association 'null'?
      case ActionsOperators.FORMAT:
        return ((selectedFieldMeta.metaType === 'STRING' || selectedFieldMeta.metaType === 'ADDRESS') && !selectedFieldMeta.readOnly);
      case ActionsOperators.APPEND_ENTRY:
        return ((selectedFieldMeta.metaType === 'TO_MANY') && this.supportAppendEntriesFields(selectedFieldMeta)
          && !selectedFieldMeta.readOnly);
      case ActionsOperators.CONCATENATE:
        return (selectedFieldMeta.metaType === 'STRING') && !selectedFieldMeta.readOnly;
      case ActionsOperators.AIASSIST:
        // TODO: work out the actual criteria here - char limit? is this even necessary? field is selected in AIA.
        return selectedFieldMeta.metaType === 'STRING' && !selectedFieldMeta.readOnly;
      default:
        return false;
    }
  }

  public static isCompatibleToOneCopy(srcToOneType: string, srcOptionsType: string | null, destToOneType: string): boolean {
    if (this.personSubKinds.includes(srcToOneType)) {
      return destToOneType === srcToOneType || destToOneType === 'Person';
    }
    if (srcToOneType === 'Person') {
      if (destToOneType === 'Person') {
        return true;
      }
      return this.personSubKinds.includes(srcOptionsType)
        && destToOneType === srcOptionsType;
    }
    return destToOneType === srcToOneType;
  }

  private static isNotReadOnly(meta: IActionFieldMeta) {
    return !meta || !meta.readOnly;
  }

  private static canCopyNullValues(srcMeta: IActionFieldMeta, dstMeta: IActionFieldMeta) {
    return !srcMeta.optional || !dstMeta || dstMeta.optional;
  }

  private static bothOrNeitherAreOptional(srcMeta: IActionFieldMeta, dstMeta: IActionFieldMeta) {
    return this.bothAreOptional(srcMeta, dstMeta) || this.neitherIsOptional(srcMeta, dstMeta);
  }

  private static neitherIsOptional(srcMeta: IActionFieldMeta, dstMeta: IActionFieldMeta) {
    return !srcMeta.optional && (!dstMeta || !dstMeta.optional);
  }

  private static bothAreOptional(srcMeta: IActionFieldMeta, dstMeta: IActionFieldMeta) {
    return srcMeta.optional && (!dstMeta || dstMeta.optional);
  }

  private static isScalar(meta: IActionFieldMeta) {
    return meta && this.scalarMetaTypes.includes(meta.metaType);
  }

  private static supportedReplaceEntriesFields(meta: IActionFieldMeta): boolean {
    return this.replaceEntriesSupportedFields.includes(meta.fieldName);
  }

  private static supportAppendEntriesFields(meta: IActionFieldMeta): boolean {
    return this.appendEntriesSupportedFields.includes(meta.toManyType);
  }

  private static notSupportedChangeEntriesField(meta: IActionFieldMeta): boolean {
    return this.changeEntriesNotSupportedFields.includes(meta.toOneType);
  }

  private static readonly personSubKinds = ['Candidate', 'ClientContact', 'Lead', 'CorporateUser'];

  private static readonly scalarMetaTypes = ['STRING', 'BOOLEAN', 'INTEGER', 'DOUBLE', 'FLOAT', 'BIG_DECIMAL', 'DATE'];

  private static readonly appendEntriesSupportedFields = ['Skill', 'Category', 'BusinessSector', 'Specialty', 'CorporateUser'];

  private static readonly replaceEntriesSupportedFields = ['isDeleted'];

  private static readonly changeEntriesNotSupportedFields = ['ClientContact'];
}
